import { faClone, faRightLeft } from "@fortawesome/pro-duotone-svg-icons"
import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { dateAdd, getIntervalDurationInDays, isDateBefore } from "app/util/date/tools/common"
import {
  CAMPAIGN_LANGUAGES,
  CAMPAIGN_STATUSES,
  DEFAULT_CAMPAIGN_LANGUAGE,
  ICampaign,
  ICampaignUpdateParams,
  IDuplicateFormData
} from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { campaignActionCreator } from "campaign/store/actions"
import { FormikErrors } from "formik"
import { messageSVC } from "message/service"

export function openDuplicatePopup(campaign: ICampaign, isForecastConvert?: boolean): void {
  getStore().dispatch(
    campaignActionCreator.setCampaignDuplicate({
      campaign,
      isForecastConvert: isForecastConvert || false,
      isSaving: false
    })
  )
}

export function setDuplicatePopupIsSaving(isSaving: boolean): void {
  getStore().dispatch(campaignActionCreator.setCampaignDuplicate({ isSaving }))
}

export function closeDuplicatePopup(): void {
  getStore().dispatch(
    campaignActionCreator.setCampaignDuplicate({
      campaign: null,
      isForecastConvert: false
    })
  )
}

export function getDefaultDuplicateForm(campaign: ICampaign, isForecastConvert?: boolean): IDuplicateFormData {
  let startDate = campaign.start_date ? new Date(campaign.start_date) : null
  let endDate = campaign.end_date ? new Date(campaign.end_date) : null
  if (startDate instanceof Date && endDate instanceof Date) {
    const today = new Date()
    if (isDateBefore(startDate, today)) {
      const numberOfDaysInterval = getIntervalDurationInDays(endDate, startDate)
      startDate = today
      endDate = dateAdd(today, numberOfDaysInterval, "days")
    }
  }
  return {
    order_id: campaign.order_id,
    advertiser_id: campaign.advertiser_id,
    language_id: (campaign.language_id as CAMPAIGN_LANGUAGES) || DEFAULT_CAMPAIGN_LANGUAGE,
    startDate,
    endDate,
    statusId: isForecastConvert
      ? CAMPAIGN_STATUSES.PENDING
      : campaign.status_id === CAMPAIGN_STATUSES.PLANNING
        ? CAMPAIGN_STATUSES.PLANNING
        : CAMPAIGN_STATUSES.PENDING
  }
}

export function validateDuplicateFormData(
  isForecast: boolean,
  isDspDeal: boolean,
  { advertiser_id, endDate, language_id, order_id, startDate }: IDuplicateFormData
): FormikErrors<IDuplicateFormData> {
  const errors: FormikErrors<IDuplicateFormData> = {}
  if (!isForecast) {
    if (advertiser_id === null && !isDspDeal) {
      errors.advertiser_id = translate("label.field.required")
    }
    if (order_id === null) {
      errors.order_id = translate("label.field.required")
    }
  }
  if (language_id === null) {
    errors.language_id = translate("label.field.required")
  }
  if (!startDate) {
    errors.startDate = translate("label.field.required")
  } else if (!(startDate instanceof Date)) {
    errors.startDate = translate("label.field.invalid.value")
  }
  if (!endDate) {
    errors.endDate = translate("label.field.required")
  } else if (!(endDate instanceof Date)) {
    errors.endDate = translate("label.field.invalid.value")
  }
  if (startDate && endDate && endDate < startDate) {
    errors.endDate = translate("label.field.date.start.greater.than.end")
  }
  return errors
}

export function duplicateCampaignOrForecast(
  campaign: ICampaign,
  isForecastConvert: boolean,
  params: IDuplicateFormData
): Promise<ICampaign | null> {
  return new Promise<ICampaign | null>(async (resolve, reject) => {
    const isForecast = campaign.status_id === CAMPAIGN_STATUSES.PLANNING
    messageSVC.confirmMessage({
      icon: isForecastConvert ? faRightLeft : faClone,
      title: translate(
        isForecastConvert
          ? "forecast.convert.confirm.title"
          : isForecast
            ? "forecast.duplicate.confirm.title"
            : "campaign.duplicate.confirm.title"
      ),
      addGenericConfirmMessage: true,
      message: translate(
        isForecastConvert
          ? "forecast.convert.confirm.message"
          : isForecast
            ? "forecast.duplicate.confirm.title"
            : "campaign.duplicate.confirm.message"
      ),
      onSuccess: () => {
        processDuplicate(campaign, params, isForecastConvert)
          .then(newCampaign => resolve(newCampaign))
          .catch(error => {
            console.error(error)
            messageSVC.error(
              translate(isForecastConvert ? "forecast.convert.error.unknown" : "campaign.duplicate.error.unknown")
            )
            reject(error)
          })
      },
      onCancel: () => resolve(null)
    })
  })
}

function processDuplicate(
  campaign: ICampaign,
  parameters: IDuplicateFormData,
  isForecastConvert: boolean
): Promise<ICampaign> {
  return new Promise<ICampaign>(async (resolve, reject) => {
    try {
      const isForecast = campaign.status_id === CAMPAIGN_STATUSES.PLANNING
      const { advertiser_id, endDate, language_id, order_id, startDate } = parameters
      let { statusId } = parameters
      let newCampaign = await campaignRPO.duplicate(campaign.id)
      const params: ICampaignUpdateParams = {
        id: newCampaign.id,
        language_id: language_id as any
      }
      if (typeof order_id === "number") {
        params.order_id = order_id
      }
      if (typeof advertiser_id === "number") {
        params.advertiser_id = advertiser_id
      }
      if (startDate instanceof Date) {
        params.start_date = startDate.toISOString()
      }
      if (endDate instanceof Date) {
        params.end_date = endDate.toISOString()
      }
      if (!isForecastConvert && isForecast) {
        statusId = CAMPAIGN_STATUSES.PLANNING
      }
      if (statusId !== undefined) {
        params.status_id = statusId
      }
      newCampaign = await campaignRPO.update(params)
      resolve(newCampaign)
    } catch (error) {
      console.error(error)
      reject("An error occured while creating or duplicating a new campaign or forecast")
    }
  })
}
